import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import { useProjectsProjectPhases } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { projectRevenueReportQueries } from "~/modules/project/api/projectRevenueReport/projectRevenueReportQueries.ts";
import {
	ProjectRevenueQuotaSection,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueQuotaSection/ProjectRevenueQuotaSection.tsx";
import ProjectRevenueStatsCard
	from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueStatsCard/ProjectRevenueStatsCard.tsx";
import ProjectRevenueTimeline
	from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueTimeline/ProjectRevenueTimeline.tsx";
import LoadingPage from "~/pages/LoadingPage.tsx";

type ProjectRevenueReportSectionProps = {
	projectId: string;
};

function byId<T extends { id: string }>(items: T[]): Record<string, T> {
	return items.reduce<Record<string, T>>((map, item) => {
		map[item.id] = item;
		return map;
	}, {});
}

const ProjectRevenueReportSection: FunctionComponent<ProjectRevenueReportSectionProps> = ({ projectId }) => {
	const { data, options } = useAggregatedDataV2({
		report: useQuery(projectRevenueReportQueries.byProject(projectId)),
		phases: useProjectsProjectPhases(projectId),
		orders: useQuery(orderQueries.byProject(projectId)),
	});

	const { report, phases, orders } = data;

	const phasesById: Record<string, ProjectPhase> = useMemo(() => byId(phases ?? []), [phases]);
	const ordersById: Record<string, OrderWithBillingSettings> = useMemo(() => byId(orders ?? []), [orders]);

	if (options.loadingPercent < 100 || !report) {
		return <LoadingPage />;
	}

	return <div className="auto-grid-2-rows h-full gap-y-2 overflow-hidden pb-8">
		<ProjectRevenueStatsCard report={report} />
		{/* The card stays fixed. The quota section and the table scroll together. */}
		<div className="flex flex-col gap-y-2 overflow-y-auto">
			{report.quotas.length > 0 && <ProjectRevenueQuotaSection quotas={report.quotas}
																	 projectId={projectId} />}
			<ProjectRevenueTimeline report={report}
									projectId={projectId}
									phasesById={phasesById}
									ordersById={ordersById} />
		</div>
	</div>;
};

export default ProjectRevenueReportSection;
